/**
 * AssetManager - Manages loading and caching of SVG assets.
 */

import type {
  AssetManifest,
  AssetCategory,
  AssetEntry,
  AssetReference,
} from '../types';
import { UNKNOWN_ASSET_SVG } from './UnknownAsset';

/**
 * Manages SVG asset loading, caching, and lookup.
 */
export class AssetManager {
  private manifest: AssetManifest | null = null;
  private assetCache: Map<string, string> = new Map();
  private loadingPromises: Map<string, Promise<string>> = new Map();

  /**
   * Create a new AssetManager instance.
   */
  constructor(private basePath: string = '') {}

  /**
   * Load an asset manifest from a URL or object.
   */
  async loadManifest(manifestOrUrl: AssetManifest | string): Promise<void> {
    if (typeof manifestOrUrl === 'string') {
      const response = await fetch(manifestOrUrl);
      if (!response.ok) {
        throw new Error(`Failed to load manifest: ${response.statusText}`);
      }
      this.manifest = await response.json();
    } else {
      this.manifest = manifestOrUrl;
    }

    if (this.manifest?.basePath) {
      this.basePath = this.manifest.basePath;
    }
  }

  /**
   * Get the loaded manifest.
   */
  getManifest(): AssetManifest | null {
    return this.manifest;
  }

  /**
   * Get all categories from the manifest.
   */
  getCategories(): AssetCategory[] {
    if (!this.manifest?.categories) return [];
    return Object.values(this.manifest.categories);
  }

  /**
   * Get a specific category by ID.
   */
  getCategory(categoryId: string): AssetCategory | undefined {
    if (!this.manifest?.categories) return undefined;
    return this.manifest.categories[categoryId.toLowerCase()];
  }

  /**
   * Get an asset entry by category and name.
   */
  getAssetEntry(category: string, name: string): AssetEntry | undefined {
    const cat = this.getCategory(category);
    return cat?.assets.find(
      (a) =>
        a.id.toLowerCase() === name.toLowerCase() ||
        a.name.toLowerCase() === name.toLowerCase()
    );
  }

  /**
   * Generate a cache key for an asset reference.
   */
  private getCacheKey(ref: AssetReference): string {
    return `${ref.category}/${ref.name}`;
  }

  /**
   * Whether a reference names an asset the loaded manifest does not hold, by category or
   * by id. Only a loaded manifest can say an id is missing: before one is loaded nothing
   * is known, so this answers false and the lookups keep their not-loaded behaviour.
   * An asset registered inline is known even without a manifest entry.
   */
  isUnknown(ref: AssetReference): boolean {
    return (
      this.manifest !== null &&
      !this.assetCache.has(this.getCacheKey(ref)) &&
      !this.getAssetEntry(ref.category, ref.name)
    );
  }

  /**
   * Load an SVG asset by reference.
   * Returns the SVG content as a string.
   *
   * A reference the manifest does not hold answers the question-mark glyph and never
   * throws (rule 12, D-150): a stored drawing naming such an id opens with a question
   * mark in that opening. It is not cached, so a manifest loaded later that does hold
   * the id resolves it. A fetch that fails for an id the manifest DOES hold still
   * rejects: that is a broken deployment, not an unknown drawing, and the caller says so.
   */
  async loadAsset(ref: AssetReference): Promise<string> {
    const cacheKey = this.getCacheKey(ref);

    // Return cached if available
    if (this.assetCache.has(cacheKey)) {
      return this.assetCache.get(cacheKey)!;
    }

    // Return existing loading promise if in progress
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey)!;
    }

    if (this.isUnknown(ref)) {
      return UNKNOWN_ASSET_SVG;
    }

    // Find the asset entry. Absent here only when no manifest is loaded yet.
    const entry = this.getAssetEntry(ref.category, ref.name);
    if (!entry) {
      throw new Error(
        `Asset not found (no manifest loaded): ${ref.category}/${ref.name}`
      );
    }

    // Start loading
    const loadPromise = this.fetchAsset(entry.file);
    this.loadingPromises.set(cacheKey, loadPromise);

    try {
      const svg = await loadPromise;
      this.assetCache.set(cacheKey, svg);
      return svg;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  /**
   * Fetch an SVG file from the given path.
   */
  private async fetchAsset(filePath: string): Promise<string> {
    const url = this.basePath ? `${this.basePath}/${filePath}` : filePath;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to load asset: ${url} (${response.statusText})`);
    }

    return response.text();
  }

  /**
   * Preload all assets in a category.
   */
  async preloadCategory(categoryId: string): Promise<void> {
    const category = this.getCategory(categoryId);
    if (!category) return;

    await Promise.all(
      category.assets.map((asset) =>
        this.loadAsset({ category: categoryId, name: asset.id })
      )
    );
  }

  /**
   * Preload all assets in the manifest.
   */
  async preloadAll(): Promise<void> {
    if (!this.manifest) return;

    const categories = Object.values(this.manifest.categories);
    await Promise.all(categories.map((cat) => this.preloadCategory(cat.id)));
  }

  /**
   * Clear the asset cache.
   */
  clearCache(): void {
    this.assetCache.clear();
  }

  /**
   * Check if an asset is cached.
   */
  isCached(ref: AssetReference): boolean {
    return this.assetCache.has(this.getCacheKey(ref));
  }

  /**
   * Get a cached asset synchronously (returns undefined if not cached).
   *
   * A reference the loaded manifest does not hold answers the question-mark glyph, the
   * same as loadAsset, so the synchronous render draws it too rather than leaving the
   * opening empty. Nothing is fetched for it, so it is always available.
   */
  getCached(ref: AssetReference): string | undefined {
    const cached = this.assetCache.get(this.getCacheKey(ref));
    if (cached !== undefined) return cached;
    return this.isUnknown(ref) ? UNKNOWN_ASSET_SVG : undefined;
  }

  /**
   * Register an inline SVG asset (useful for testing or embedded assets).
   */
  registerInlineAsset(category: string, name: string, svg: string): void {
    const cacheKey = `${category}/${name}`;
    this.assetCache.set(cacheKey, svg);
  }
}

/**
 * The entries of a category that are offered for NEW drawings: every entry not
 * flagged `retired`, in manifest order.
 *
 * Why this lives here, beside the manager, and not inside the editor: retirement
 * is a rule about the drawing set, and it has two halves that must not drift
 * apart. Offering is filtered here; resolving is deliberately NOT, so
 * `getAssetEntry` and `loadAsset` keep finding a retired id and every drawing
 * saved with one keeps rendering for as long as it stays retired. Once the
 * architect has it erased, the same lookups answer the question mark for it,
 * and the stored drawing is still never rewritten (rule 5, D-150).
 * Any surface that lists assets for choosing calls this; any surface that draws
 * a stored reference goes through the manager's lookups instead.
 */
export function offeredAssets(category: AssetCategory): AssetEntry[] {
  return (category.assets ?? []).filter((asset) => !asset.retired);
}

/** One family of drawings as the picker lists it: its name and its entries. */
export interface AssetFamily {
  family: string;
  assets: AssetEntry[];
}

/**
 * Group entries by their `family` field, in the order each family first appears,
 * keeping each family's entries in manifest order (D-152, rule 14). The picker
 * decides heading-or-row from each group's size, so grouping reads the field and
 * never the display name: adding a second drawing to a family is what turns its
 * row into a heading, with no other change.
 *
 * An entry that arrives without a family is its own family of one, keyed by its
 * id, so it lists as a legible full-name row. That happens only when a newer
 * bundle reads an older manifest (the service worker precaches the manifest,
 * so after a deploy the old copy can answer until the update gate appears);
 * nothing is worked out from the name even then.
 */
export function groupByFamily(assets: AssetEntry[]): AssetFamily[] {
  const groups = new Map<string, AssetEntry[]>();
  for (const asset of assets) {
    // Typed as required, but the manifest is JSON cast at load (see above).
    const family = (asset.family as string | undefined) ?? `id:${asset.id}`;
    const members = groups.get(family);
    if (members) members.push(asset);
    else groups.set(family, [asset]);
  }
  return [...groups].map(([family, members]) => ({
    family,
    assets: members,
  }));
}

/**
 * Create a default asset manager instance.
 */
export function createAssetManager(basePath?: string): AssetManager {
  return new AssetManager(basePath);
}
